import { Component, OnDestroy, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { SubtitleChunk } from '@app/store/audio';
import { AudioFacade } from '@app/store/audio';
import { MarkdownPipe } from '@app/pipes/markdown.pipe';
import { VideoSearchFacade } from '@app/store/video-search';
import { VideoSearchResult } from '@app/store/video-search';

@Component({
  selector: 'app-subtitle',
  templateUrl: './subtitle.component.html',
  styleUrls: ['./subtitle.component.scss'],
  standalone: true,
  imports: [CommonModule, MarkdownPipe]
})
export class SubtitleComponent implements OnDestroy {
  public currentMessage = signal<SubtitleChunk | null>(null);
  public isVisible = signal(true);
  public videoSearchResults = signal<VideoSearchResult[]>([]);
  public showVideoLink = signal(false);
  private linkVisibleFromStore = signal(false);

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly audioFacade: AudioFacade,
    private readonly videoSearchFacade: VideoSearchFacade
  ) {
    this.initializeSubscriptions();
  }

  private initializeSubscriptions(): void {
    // Subscribe to current subtitle changes (using new NgRx selector)
    this.audioFacade.currentDisplayedChunk$
      .pipe(takeUntil(this.destroy$))
      .subscribe(chunk => {
        this.currentMessage.set(chunk);
        this.updateVideoLinkVisibility();
      });

    // Subscribe to visibility changes
    this.audioFacade.isSubtitleEnabled$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isEnabled => {
        this.isVisible.set(isEnabled);
        this.updateVideoLinkVisibility();
      });

    // Subscribe to video search results
    this.videoSearchFacade.searchResults$
      .pipe(takeUntil(this.destroy$))
      .subscribe(results => {
        this.videoSearchResults.set(results);
        this.updateVideoLinkVisibility();
      });

    // Subscribe to video link visibility from the store
    this.videoSearchFacade.linkVisible$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isVisible => {
        this.linkVisibleFromStore.set(isVisible);
        this.updateVideoLinkVisibility();
      });
  }

  /**
   * Returns the text to display in the subtitle
   */
  public displayedText = computed(() => this.currentMessage()?.content || '');

  /**
   * Determines if the subtitle should be shown
   */
  public shouldShow = computed(() => 
    this.isVisible() &&
    !!this.currentMessage() &&
    this.currentMessage()!.role === 'assistant' &&
    !!this.currentMessage()!.content?.trim()
  );

  /**
   * Updates video link visibility based on subtitle state and search results
   */
  private updateVideoLinkVisibility(): void {
    if (!this.linkVisibleFromStore()) {
      this.showVideoLink.set(false);
      return;
    }

    const hasResults = this.videoSearchResults().length > 0;
    if (!hasResults) {
      this.showVideoLink.set(false);
      return;
    }

    this.showVideoLink.set(!this.shouldShow());
  }

  /**
   * Gets the primary video result to display
   */
  public primaryVideoResult = computed(() => 
    this.videoSearchResults().length > 0 ? this.videoSearchResults()[0] : null
  );

  /**
   * Handles video link click
   */
  onVideoLinkClick(): void {
    const result = this.primaryVideoResult();
    if (result) {
      // Open the video in a new tab
      window.open(result.url, '_blank');

      // Clear the search results and hide the link after clicking
      this.videoSearchFacade.clearResults('user clicked video link');
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
